import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { Component } from 'react';
import type { Subscription } from 'rxjs';

import { PasswordChangeError } from '~/errors/PasswordChangeError';
import * as AuthService from '~/services/authentication';
import type { User } from '~/types/types';
import { nullOrWhitespace } from '~/util/use-subscription';

type AccountControlProps = {};

interface AccountControlState {
  user: User | null;
  currentPass: string;
  currentPassError: boolean;
  newPass: string;
  repeatNewPass: string;
  passFormFeedback: string;
  newPassError: boolean;
}

class AccountControls extends Component<AccountControlProps, AccountControlState> {
  userSub?: Subscription;

  constructor(props: AccountControlProps) {
    super(props);
    this.state = {
      user: null,
      currentPass: '',
      currentPassError: false,
      newPass: '',
      repeatNewPass: '',
      passFormFeedback: '',
      newPassError: false,
    };
  }

  componentDidMount() {
    this.userSub = AuthService.getUserObs().subscribe((user) => {
      if (user == null) {
        return;
      }

      this.setState({ user });
    });
  }

  componentWillUnmount() {
    this.userSub?.unsubscribe();
  }

  componentDidUpdate() {
    const nonmatchError = 'Det upprepade lösenordet stämmer inte';
    if (this.state.newPass !== this.state.repeatNewPass && this.state.passFormFeedback !== nonmatchError) {
      this.setState({ passFormFeedback: nonmatchError, newPassError: true });
    } else if (this.state.newPass === this.state.repeatNewPass && this.state.passFormFeedback === nonmatchError) {
      this.setState({ passFormFeedback: '', newPassError: false });
    }
  }

  handleCurrentPassChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentPass: e.target.value });
    if (this.state.currentPassError) {
      this.setState({ currentPassError: false, passFormFeedback: '' });
    }
  };

  handleNewPassChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ newPass: e.target.value });
  };

  handleRepeatNewPassChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ repeatNewPass: e.target.value });
  };

  handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { currentPass, newPass } = this.state;
    this.setState({ passFormFeedback: '' });

    if (this.state.user === null) {
      return;
    }
    const redirectHome = this.state.user.temporaryPassword;

    try {
      await AuthService.changePass(this.state.user.id, currentPass, newPass);
      this.setState({
        currentPass: '',
        newPass: '',
        repeatNewPass: '',
        passFormFeedback: 'Lösenord uppdaterat!',
      });

      if (redirectHome) {
        window.location.href = '/';
      }
    } catch (error: any) {
      if (error instanceof PasswordChangeError) {
        switch (error.failingPass) {
          case 'new':
            this.setState({
              newPassError: true,
              passFormFeedback: error.message,
            });
            break;
          case 'old':
            this.setState({
              currentPassError: true,
              passFormFeedback: error.message,
            });
            break;
          default:
            this.setState({ passFormFeedback: error.message });
            break;
        }
      } else {
        this.setState({ passFormFeedback: error.message });
      }
    }
  };

  render() {
    if (this.state.user === null) {
      return <Typography>No user</Typography>;
    }

    const temporaryPasswordLabel = () => {
      if (this.state.user?.temporaryPassword) {
        return (
          <Typography variant="h6" color="secondary">
            Du måste uppdatera lösenordet
          </Typography>
        );
      }

      return <Typography variant="h6">Uppdatera lösenord</Typography>;
    };

    return (
      <>
        <Grid
          direction="column"
          spacing={2}
          marginTop={2}
          container
          justifyContent="center"
          size={{ xs: 11, md: 7, lg: 4 }}
        >
          <Grid size={12}>
            <Typography textAlign={'center'} variant="h4">
              Mitt konto
            </Typography>
          </Grid>
          <Grid>
            <form autoComplete="on" onSubmit={this.handleSubmit}>
              <Grid container direction={'column'} spacing={2}>
                {temporaryPasswordLabel()}
                <Grid>
                  <TextField
                    variant="standard"
                    fullWidth={true}
                    label="Nuvarande lösenord"
                    name="password"
                    type="password"
                    id="password"
                    value={this.state.currentPass}
                    onChange={this.handleCurrentPassChange}
                    error={this.state.currentPassError}
                  />
                </Grid>
                <Grid>
                  <TextField
                    variant="standard"
                    fullWidth={true}
                    label="Nytt lösenord"
                    name="newpass"
                    type="password"
                    id="newpass"
                    value={this.state.newPass}
                    onChange={this.handleNewPassChange}
                    error={this.state.newPassError}
                  />
                </Grid>
                <Grid>
                  <TextField
                    variant="standard"
                    fullWidth={true}
                    label="Upprepa lösenord"
                    name="repeatnewpass"
                    type="password"
                    id="repeatnewpass"
                    value={this.state.repeatNewPass}
                    onChange={this.handleRepeatNewPassChange}
                    error={this.state.newPassError}
                  />
                </Grid>
                <Grid container spacing={1}>
                  <Grid size={10}>
                    <Typography color={this.state.currentPassError || this.state.newPassError ? 'error' : 'initial'}>
                      {this.state.passFormFeedback}
                    </Typography>
                  </Grid>
                  <Grid size={2} textAlign={'end'}>
                    <Button
                      color="primary"
                      variant="contained"
                      type="submit"
                      value="changepass"
                      disabled={
                        this.state.currentPassError || this.state.newPassError || nullOrWhitespace(this.state.newPass)
                      }
                    >
                      Spara
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
          <Grid>
            <Typography variant="h6">Användaruppgifter</Typography>
            <table>
              <tbody>
                <tr>
                  <td>Namn:</td>
                  <td>{this.state.user.name}</td>
                </tr>
                <tr>
                  <td>Användarnamn:</td>
                  <td>{this.state.user.username}</td>
                </tr>
                <tr>
                  <td>Mina roller:</td>
                  <td>{this.state.user.roles.sort().join(', ')}</td>
                </tr>
              </tbody>
            </table>
          </Grid>
        </Grid>
      </>
    );
  }
}

export default AccountControls;
